import React, { useState, useEffect } from 'react';
import { PlusCircle, Building2, Calendar, DollarSign, MapPin, Edit, Eye, Filter, Search, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { JVSubmissionForm } from '../JVSubmissionForm';
import { getJVProperties, updateJVProperty, JVPropertyResponse } from '../../utils/api';
import { fuzzyMatch } from '../../utils/fuzzySearch';
import { JVSubmissionDetailsModal } from '../JVSubmissionDetailsModal';
import { wsService } from '../../services/WebSocketService';

interface JVPortalViewProps {
  initialParams?: {
    submissionId?: string;
    scrollToComments?: boolean;
  } | null;
}

export function JVPortalView({ initialParams }: JVPortalViewProps = {}) {
  const [showJVForm, setShowJVForm] = useState(false);
  const [submissions, setSubmissions] = useState<JVPropertyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<JVPropertyResponse | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const [scrollToComments, setScrollToComments] = useState(initialParams?.scrollToComments || false);
  const [handledInitialSubmissionId, setHandledInitialSubmissionId] = useState<string | null>(null);

  useEffect(() => {
    loadSubmissions();
    loadCurrentUser();
  }, []);

  // Listen for WebSocket updates to JV submissions
  useEffect(() => {
    const handleNotification = (notification: any) => {
      // Check if this is a JV submission notification
      if (notification.type === 'jv_submission_update' || notification.type === 'jv_status_changed') {
        console.log('JV submission updated via WebSocket, refreshing list');
        loadSubmissions(); // Refresh submissions to update stats
      }
    };

    wsService.on('notification', handleNotification);

    return () => {
      wsService.off('notification', handleNotification);
    };
  }, []);

  // Handle initial navigation with submissionId (from notification)
  useEffect(() => {
    const submissionId = initialParams?.submissionId;

    // Only open if we haven't handled this submissionId yet
    if (submissionId && submissions.length > 0 && handledInitialSubmissionId !== submissionId) {
      const submission = submissions.find(s => s.id === submissionId);
      if (submission) {
        setSelectedSubmission(submission);
        setModalMode('view');
        setScrollToComments(initialParams?.scrollToComments || false);
        setHandledInitialSubmissionId(submissionId); // Mark as handled
      } else {
        console.warn(`Submission ${submissionId} not found`);
        setHandledInitialSubmissionId(submissionId); // Mark as handled even if not found
      }
    }
  }, [initialParams?.submissionId, submissions, handledInitialSubmissionId]);

  const loadCurrentUser = async () => {
    try {
      const session = await fetchAuthSession();
      const email = session.tokens?.idToken?.payload.email as string;
      setCurrentUserEmail(email || '');
    } catch (err) {
      console.error('Failed to get current user:', err);
    }
  };

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getJVProperties();
      setSubmissions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSubmission = async (updatedSubmission: any) => {
    try {
      await updateJVProperty(updatedSubmission.id, updatedSubmission);
      // Reload submissions to get fresh data
      await loadSubmissions();
      setSelectedSubmission(null);
    } catch (err) {
      throw err; // Let modal handle error display
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'under_review':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'under_review':
        return <TrendingUp className="w-4 h-4" />;
      case 'rejected':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const filteredSubmissions = submissions.filter(submission =>
    fuzzyMatch(
      searchQuery,
      submission.propertyStreet,
      submission.propertyCity,
      submission.propertyState
    )
  );

  const stats = {
    total: submissions.length,
    pending: submissions.filter(s => s.status?.toLowerCase() === 'pending').length,
    approved: submissions.filter(s => s.status?.toLowerCase() === 'approved').length,
    underReview: submissions.filter(s => s.status?.toLowerCase() === 'under_review').length,
  };

  // Calculate total ARV value
  const totalValue = submissions.reduce((sum, s) => {
    const arv = s.arv?.replace(/[$,]/g, '') || '0';
    return sum + (Number(arv) || 0);
  }, 0);

  const handleFormClose = () => {
    setShowJVForm(false);
    loadSubmissions(); // Reload submissions after form closes
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-tdb-primary">Joint Venture Portal</h1>
          <p className="text-gray-600 mt-1">Manage and track your JV property submissions</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowJVForm(true)}
            className="flex items-center space-x-2 px-6 py-3 bg-tdb-accent text-white rounded-xl hover:bg-[#0ea5ef] transition-all shadow-lg hover:shadow-xl font-bold"
          >
            <PlusCircle className="w-5 h-5" />
            <span>Submit New Property</span>
          </button>
        </div>
      </div>

      {/* Search - Only show if there are submissions */}
      {submissions.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by address, city, or state..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            />
          </div>
        </div>
      )}

      {/* Submissions List */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <h2 className="text-2xl font-bold text-tdb-primary">Your Submissions</h2>
          <p className="text-sm text-gray-500 mt-1">
            {filteredSubmissions.length > 0
              ? `${filteredSubmissions.length} ${filteredSubmissions.length === 1 ? 'property' : 'properties'} found`
              : 'Manage and track your property submissions'}
          </p>
        </div>

        {loading ? (
          <div className="px-8 py-16 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-tdb-accent"></div>
            <p className="text-gray-600 mt-4">Loading submissions...</p>
          </div>
        ) : error ? (
          <div className="px-8 py-16 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Error Loading Submissions</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={loadSubmissions}
              className="px-6 py-3 bg-tdb-accent text-white rounded-xl hover:bg-[#0ea5ef] transition-all font-semibold"
            >
              Try Again
            </button>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="px-8 py-16 text-center">
            <div className="inline-block p-6 bg-tdb-accent/10 rounded-full mb-6">
              <Building2 className="w-20 h-20 text-tdb-accent" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">
              {submissions.length === 0 ? 'No Submissions Yet' : 'No Matching Submissions'}
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto text-lg">
              {submissions.length === 0
                ? 'Get started by submitting your first joint venture property and begin building your portfolio'
                : 'Try adjusting your search or filter criteria.'}
            </p>
            {submissions.length === 0 && (
              <button
                onClick={() => setShowJVForm(true)}
                className="inline-flex items-center space-x-3 px-8 py-4 bg-tdb-primary hover:bg-tdb-secondary text-white rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
              >
                <PlusCircle className="w-6 h-6" />
                <span>Submit Your First Property</span>
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="px-8 py-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <button
                        onClick={() => { setSelectedSubmission(submission); setModalMode('view'); }}
                        className="text-lg font-bold text-tdb-primary hover:text-tdb-accent hover:underline transition-colors text-left"
                      >
                        {submission.propertyStreet}
                      </button>
                      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(submission.status)}`}>
                        {getStatusIcon(submission.status)}
                        <span>{submission.status?.replace('_', ' ').toUpperCase()}</span>
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" />
                        <span>{submission.propertyCity}, {submission.propertyState} {submission.propertyZip}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>Submitted: {new Date(submission.createdAt).toLocaleDateString()}</span>
                      </div>
                      {submission.arv && (
                        <div className="flex items-center space-x-1">
                          <DollarSign className="w-4 h-4" />
                          <span>ARV: {submission.arv}</span>
                        </div>
                      )}
                    </div>

                    {submission.notes && (
                      <p className="text-sm text-gray-500 line-clamp-2">{submission.notes}</p>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setModalMode('view');
                    }}
                    className="ml-4 flex items-center space-x-2 px-4 py-2 bg-tdb-accent text-white rounded-xl hover:bg-[#0ea5ef] transition-all font-semibold"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View Details</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* JV Submission Form Modal */}
      {showJVForm && <JVSubmissionForm onClose={handleFormClose} />}

      {/* Submission Details Modal */}
      {selectedSubmission && (
        <JVSubmissionDetailsModal
          submission={selectedSubmission}
          isOpen={!!selectedSubmission}
          onClose={() => {
            setSelectedSubmission(null);
            setModalMode('view');
            setScrollToComments(false); // Reset scroll flag
          }}
          onSave={handleSaveSubmission}
          mode={modalMode}
          currentUserEmail={currentUserEmail}
          scrollToComments={scrollToComments}
        />
      )}
    </div>
  );
}
